import { useState } from 'react';
import {
  CharlieDialog,
  CharlieDialogContent,
  CharlieButton,
} from '@ver.id/libs.ui.charlie';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { type RequestedDataGroup } from '../../hooks/useRequestedData';
import { useTranslation } from '../../i18n/i18n';
import HostedDataTableLayout from './DataTableLayout';
import HostedDataTable from './DataTable';

type Props = {
  groups: RequestedDataGroup[];
  variant: 'mobile' | 'desktop';
  onClose: () => void;
};

/**
 * HostedRequestedDataModal — Dialog showing all requested/issued data fields.
 *
 * Desktop: credential tab sidebar (left) + attribute table (right).
 * Mobile:  credential selector dropdown + attribute table + close button.
 *
 * Figma screens:
 *   Desktop:  https://www.figma.com/design/6V1D3oNy17pfFNHIRb2nmm/Ronan-V2---UX?node-id=131-13369
 *   Mobile:   https://www.figma.com/design/6V1D3oNy17pfFNHIRb2nmm/Ronan-V2---UX?node-id=176-22671
 *   Dropdown: https://www.figma.com/design/6V1D3oNy17pfFNHIRb2nmm/Ronan-V2---UX?node-id=176-23476
 */
export default function HostedRequestedDataModal({
  groups,
  variant,
  onClose,
}: Props) {
  const { t } = useTranslation();
  const [selectedIndex, setSelectedIndex] = useState(0);

  return (
    <CharlieDialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <CharlieDialogContent
        hideClose
        className={
          variant === 'mobile'
            ? 'max-w-full m-0 rounded-none h-full'
            : 'max-w-3xl'
        }
      >
        {/* Close button — absolute top-right */}
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 z-10 flex items-center justify-center rounded-sm text-foreground/70 transition-opacity hover:opacity-100"
          aria-label="Close"
        >
          <FontAwesomeIcon icon={faXmark} className="size-4" />
        </button>

        {/* Header */}
        <div className="-mb-4 flex flex-col gap-2 pr-6">
          <h2 className="text-2xl font-semibold text-foreground">
            {t('requestedDataModal.title')}
          </h2>
          <p className="text-sm text-muted-foreground">
            {t('requestedDataModal.subtitle')}
          </p>
        </div>

        <HostedDataTableLayout
          groups={groups}
          selectedIndex={selectedIndex}
          onSelectIndex={setSelectedIndex}
          variant={variant}
        >
          {(activeGroup) => <AttributeTable group={activeGroup} />}
        </HostedDataTableLayout>

        {/* Mobile close button */}
        {variant === 'mobile' && (
          <CharlieButton variant="outline" pill block onClick={onClose}>
            {t('requestedDataModal.close')}
          </CharlieButton>
        )}
      </CharlieDialogContent>
    </CharlieDialog>
  );
}

/** Two-column attribute table: Attribute | Description */
function AttributeTable({ group }: { group: RequestedDataGroup }) {
  const { t } = useTranslation();

  return (
    <HostedDataTable
      group={group}
      columns={[
        { header: t('requestedDataModal.attribute'), render: (f) => f.name },
        {
          header: t('requestedDataModal.description'),
          render: (f) => f.description,
          className: 'italic text-foreground',
        },
      ]}
    />
  );
}
